/**
* Google Analytics Tracking Service
*
* This service handles pushing events to Google Tag Manager's dataLayer.
* All events are automatically enriched with user info, screen info, and previous screen info.
*
* @example
* // Track a custom event
* import { trackEvent } from './services/gaTracking';
* import { GA_EVENTS } from './constants/gaEvents';
*
* trackEvent(GA_EVENTS.LOGIN);
*
* // Track with additional data
* trackEvent(GA_EVENTS.LOGIN, { userName: "John Doe", businessModule: "Sales" });
*
* // Track with target URL (for navigation events)
* trackEvent(GA_EVENTS.SALES_CLICK, { lastLoggedIn: "..." }, "/digital/sales");
*/

import { checkIsAdmin } from "../helper/utils";

 
const getUserDetails = () => {
  try {
    const userDetails = sessionStorage.getItem("user_details");
    if (userDetails) {
      return JSON.parse(userDetails);
    }
  } catch (error) {
    console.error("Error parsing user details:", error);
  }
  return null;
};
 
const getSessionId = () => {
  try {
    let sessionId = sessionStorage.getItem("ga_session_id");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("ga_session_id", sessionId);
    }
    return sessionId;
  } catch (error) {
    return null;
  }
};
 
const getPlatform = () => {
  if (typeof window !== "undefined") {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) return "Android";
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
    if (/Mac|Windows|Linux/.test(userAgent)) return "Web";
  }
  return "Web";
};
 
const getScreenInfo = (pathname) => {
  const screenName = pathname || window.location.pathname;
  
  const screenTypeMap = {
    "/oneapp/home": "home",
    "/oneapp/dashboard": "dashboard",
    "/oneapp/admin": "admin",
    "/oneapp/leads": "leads",
    "/oneapp/product-selector": "product-selector",
    "/oneapp/kn-reports": "kn-reports",
    "/digital/sales": "sales",
    "/digital/discount": "discount",
    "/digital/delivery-installation": "delivery-installation",
    "/digital/in-stock": "in-stock",
    "/digital/pmi": "pmi",
    "/quickapp/daily-tracker": "daily-tracker",
    "/quickapp/store-task": "store-task",
    "/quickapp/StuRaiseTicket": "raise-ticket",
    "/quickapp": "quick-assist",
  };
 
  let screenType = "unknown";
  for (const [path, type] of Object.entries(screenTypeMap)) {
    if (screenName.includes(path)) {
      screenType = type;
      break;
    }
  }
 
  return {
    screenName,
    screenType,
  };
};
 
let previousScreen = {
  screenName: null,
  screenType: null,
};
 
const updatePreviousScreen = (currentScreen) => {
  previousScreen = { ...currentScreen };
};
 
const getPreviousScreen = () => {
  return {
    previousScreenName: previousScreen.screenName || "/home",
    previousScreenType: previousScreen.screenType || "home",
  };
};
 
const pushToDataLayer = (eventData) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(eventData);
    
    // Log for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔔 GA Event: ${eventData.event}`, {
        screen: eventData.screenName,
        previous: eventData.previousScreenName
      });
    }
  } else {
    console.warn("dataLayer not available", eventData);
  }
};
 
export const trackEvent = (eventConfig, additionalData = {}, targetUrl = null) => {
  const userDetails = getUserDetails();
  const currentScreen = getScreenInfo();
  const previousScreenInfo = getPreviousScreen();
 
  // Get userId from API response
  const userId = userDetails?.id || null;
  
  // isLoggedIn should be true if we have user identifier
  const isLoggedIn = !!(userDetails && userId);
 
  // Use target screen if provided (for navigation events), otherwise use current screen
  const screenInfo = targetUrl ? getScreenInfo(targetUrl) : currentScreen;
 
  // Extract fields from user details (exact API response structure)
  const employeeId = userDetails?.employeeId || null;
  const userName = userDetails?.name || userDetails?.employeeId || null;
  const designation = userDetails?.designation || userDetails?.jobRole || null;
  const jobRoleName = userDetails?.jobRole || null;
  const businessUnit = userDetails?.businesses?.[0]?.name || null;
  const vertical = userDetails?.businesses?.[0]?.vertical || null;
  const businessModule = userDetails?.businessModule || null;
  const jobRole = userDetails?.jobRole || null;
  console.log("user details",userDetails)
  
  // Check if user is HO or Head - if yes, send "HO" in location
  const isHOOrHead = checkIsAdmin(userDetails);
  
  // Get locationCode from site details stored in localStorage - only send locationCode
  let locationCode = null;
  try {
    const siteDetails = localStorage.getItem("site_details");
    if (siteDetails) {
      const parsedSiteDetails = JSON.parse(siteDetails);
      console.log("parsedSiteDetails", parsedSiteDetails);
      // Handle case where API returns null data
      if (parsedSiteDetails?.data && parsedSiteDetails.data !== null) {
        locationCode = parsedSiteDetails.data.locationCode || null;
      }
    }
  } catch (error) {
    console.error("Error parsing site details:", error);
  }
  
  // Determine location value: "HO" for HO/Head users, otherwise locationCode (or null if not available)
  // This logic applies to all events including login event
  const location = isHOOrHead ? "HO" : (locationCode || null);
  
  const platform = getPlatform();
 
  const baseEventData = {
    event: eventConfig.event,
    eventCategory: eventConfig.eventCategory || "",
    eventAction: eventConfig.eventAction || "",
    eventLabel: eventConfig.eventLabel || "",
    isLoggedIn: isLoggedIn,
    userId: userId,
    employeeId: employeeId,
    userName: userName,
    designation: designation,
    jobRoleName: jobRoleName,
    businessUnit: businessUnit,
    vertical: vertical,
    businessModule: businessModule,
    location: location,
    platform: platform,
    screenName: screenInfo.screenName,
    screenType: screenInfo.screenType,
    previousScreenName: previousScreenInfo.previousScreenName,
    previousScreenType: previousScreenInfo.previousScreenType,
  };
 
  // Merge additionalData but ensure location always uses the correct value (overrides any location in additionalData)
  const eventData = {
    ...baseEventData,
    ...additionalData,
    location: location, // Always use location (HO for HO/Head users, locationCode for others) - ensures it's never overridden
  };
 
  pushToDataLayer(eventData);
  updatePreviousScreen(screenInfo);
  
  return eventData;
};
 
export default {
  trackEvent,
};